import { Building, FileSpreadsheet } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const getPaymentRowKey = (payment, index) => (
  payment?.payment_id
  || `${payment?.po_number || 'payment'}-${payment?.paid_at || 'unknown'}-${payment?.amount || 0}-${index}`
);

const getTimelineRowKey = (event, index) => (
  event?.event_id
  || `${event?.event_type || 'event'}-${event?.po_number || event?.document_label || 'doc'}-${event?.occurred_at || 'time'}-${index}`
);

export const SupplierLedgerDialog = ({
  ledger,
  filteredTimeline = [],
  timelineFilters,
  timelinePresets = [],
  timelineStatusOptions = [],
  ledgerExporting = false,
  onExport,
  onPresetChange,
  onEventTypeChange,
  onStatusChange,
  onStartDateChange,
  onEndDateChange,
  formatCurrency,
  formatDateTime,
  statusColors,
  statusLabels,
  paymentStatusColors,
  paymentStatusLabels,
}) => {
  const summary = ledger?.summary || {};
  const purchaseOrders = ledger?.purchase_orders || [];
  const payments = ledger?.payments || [];

  return (
    <DialogContent className="w-[min(100vw-1rem,1580px)] max-w-[1580px] max-h-[94vh] overflow-hidden rounded-[28px] border border-slate-200 bg-white p-0 shadow-xl" data-testid="supplier-ledger-dialog-shell">
      <DialogHeader className="border-b border-slate-200 bg-slate-50 px-4 py-4 sm:px-6">
        <DialogTitle className="flex items-center gap-2">
          <Building className="h-5 w-5 text-emerald-700" />
          Conta Corrente do Fornecedor - {ledger?.supplier?.name}
        </DialogTitle>
        <DialogDescription className="text-sm text-slate-500">
          Reconciliação consolidada da cadeia Compras → Recepção → Apto para pagamento → Pagamento ao fornecedor.
        </DialogDescription>
      </DialogHeader>

      <div className="max-h-[80vh] overflow-y-auto px-4 py-4 sm:px-6 xl:px-8 xl:py-6">
        {ledger && (
          <div className="space-y-5">
            <div className="grid gap-3 md:grid-cols-5" data-testid="supplier-ledger-detail-summary-grid">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Total encomendado</p>
                <p className="mt-1 text-lg font-semibold text-slate-900">{formatCurrency(summary.total_ordered || 0)}</p>
              </div>
              <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3">
                <p className="text-xs uppercase tracking-[0.18em] text-blue-700">Total recebido</p>
                <p className="mt-1 text-lg font-semibold text-blue-900">{formatCurrency(summary.total_received || 0)}</p>
              </div>
              <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3">
                <p className="text-xs uppercase tracking-[0.18em] text-indigo-700">Apto para pagamento</p>
                <p className="mt-1 text-lg font-semibold text-indigo-900">{formatCurrency(summary.total_payable || 0)}</p>
              </div>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                <p className="text-xs uppercase tracking-[0.18em] text-emerald-700">Total pago</p>
                <p className="mt-1 text-lg font-semibold text-emerald-900">{formatCurrency(summary.total_paid || 0)}</p>
              </div>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-xs uppercase tracking-[0.18em] text-amber-700">Saldo em aberto</p>
                <p className="mt-1 text-lg font-semibold text-amber-900">{formatCurrency(summary.balance_due || 0)}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-slate-600">
                <span className="font-medium">{summary.po_count || 0}</span> PO(s) · <span className="font-medium">{summary.payment_events_count || 0}</span> pagamento(s)
              </div>
              <Button variant="outline" onClick={() => onExport?.(ledger?.supplier?.id)} disabled={ledgerExporting} data-testid="supplier-ledger-export-detail-button">
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                {ledgerExporting ? 'A exportar...' : 'Exportar razão do fornecedor'}
              </Button>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="supplier-ledger-pos-table">
              <h4 className="mb-3 font-medium text-slate-900">POs e reconciliação</h4>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] text-sm">
                  <thead className="border-b bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-500">PO #</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-500">Estado</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-500">Encomendado</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-500">Recebido</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-500">Apto</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-500">Pago</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-500">Saldo</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-500">Estado Financeiro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseOrders.map((po) => (
                      <tr key={po.id} className="border-b last:border-0">
                        <td className="px-3 py-2 font-medium text-slate-800">{po.po_number}</td>
                        <td className="px-3 py-2"><Badge variant="outline" className={statusColors[po.status]}>{statusLabels[po.status] || po.status}</Badge></td>
                        <td className="px-3 py-2 text-right">{formatCurrency(po.total_with_vat || 0)}</td>
                        <td className="px-3 py-2 text-right text-blue-700">{formatCurrency(po.payable_amount_total || 0)}</td>
                        <td className="px-3 py-2 text-right text-indigo-700">{formatCurrency(po.payable_amount_total || 0)}</td>
                        <td className="px-3 py-2 text-right text-emerald-700">{formatCurrency(po.paid_amount_total || 0)}</td>
                        <td className="px-3 py-2 text-right text-amber-700">{formatCurrency(po.supplier_balance_due || 0)}</td>
                        <td className="px-3 py-2"><Badge variant="outline" className={paymentStatusColors[po.payment_status] || paymentStatusColors.not_payable}>{paymentStatusLabels[po.payment_status] || paymentStatusLabels.not_payable}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="supplier-ledger-payments-table">
              <h4 className="mb-3 font-medium text-slate-900">Histórico financeiro do fornecedor</h4>
              {payments.length === 0 ? (
                <p className="text-sm text-slate-500">Sem pagamentos registados neste fornecedor.</p>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment, index) => (
                    <div key={getPaymentRowKey(payment, index)} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`supplier-ledger-payment-row-${index}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{payment.po_number} · {formatCurrency(payment.amount)}</p>
                          <p className="text-xs text-slate-500">{formatDateTime(payment.paid_at)} · {payment.payment_account_name || payment.payment_method_name || 'Conta'}</p>
                        </div>
                        {payment.reference && <Badge variant="outline">{payment.reference}</Badge>}
                      </div>
                      {payment.notes && <p className="mt-2 text-xs text-slate-500">Nota: {payment.notes}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="supplier-ledger-timeline-table">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h4 className="font-medium text-slate-900">Linha temporal / auditoria por fornecedor</h4>
                  <p className="text-xs text-slate-500">{filteredTimeline.length} evento(s) visíveis com saldo acumulado após cada movimento.</p>
                </div>
              </div>

              <div className="mb-4 grid gap-3 lg:grid-cols-[220px_200px_220px_180px_180px]" data-testid="supplier-ledger-timeline-filters">
                <div className="space-y-1.5">
                  <Label>Preset rápido</Label>
                  <Select value={timelineFilters.preset} onValueChange={onPresetChange}>
                    <SelectTrigger data-testid="supplier-ledger-timeline-preset-select">
                      <SelectValue placeholder="Período" />
                    </SelectTrigger>
                    <SelectContent>
                      {timelinePresets.map((preset) => (
                        <SelectItem key={preset.value} value={preset.value}>{preset.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Tipo de evento</Label>
                  <Select value={timelineFilters.event_type} onValueChange={onEventTypeChange}>
                    <SelectTrigger data-testid="supplier-ledger-timeline-type-select">
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="po_created">PO</SelectItem>
                      <SelectItem value="receipt">Recepção</SelectItem>
                      <SelectItem value="payment">Pagamento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Estado</Label>
                  <Select value={timelineFilters.status_filter} onValueChange={onStatusChange}>
                    <SelectTrigger data-testid="supplier-ledger-timeline-status-select">
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {timelineStatusOptions.map((status) => (
                        <SelectItem key={status} value={status}>{statusLabels[status] || paymentStatusLabels[status] || status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Data inicial</Label>
                  <Input type="date" value={timelineFilters.start_date} onChange={onStartDateChange} data-testid="supplier-ledger-timeline-start-date" />
                </div>

                <div className="space-y-1.5">
                  <Label>Data final</Label>
                  <Input type="date" value={timelineFilters.end_date} onChange={onEndDateChange} data-testid="supplier-ledger-timeline-end-date" />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[1040px] text-sm">
                  <thead className="border-b bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-500">Data</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-500">Tipo</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-500">Documento / PO</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-500">Valor do evento</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-500">Impacto no saldo</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-500">Saldo acumulado</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-500">Estado</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-500">Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTimeline.map((event, index) => (
                      <tr key={getTimelineRowKey(event, index)} className="border-b last:border-0" data-testid={`supplier-ledger-timeline-row-${index}`}>
                        <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(event.occurred_at)}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="rounded-full">
                            {event.event_type === 'po_created' ? 'PO' : event.event_type === 'receipt' ? 'Recepção' : 'Pagamento'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-800">{event.document_label || event.po_number}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(event.event_value || 0)}</td>
                        <td className={`px-3 py-2 text-right font-medium ${Number(event.impact_on_balance || 0) >= 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{formatCurrency(event.impact_on_balance || 0)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900">{formatCurrency(event.balance_after || 0)}</td>
                        <td className="px-3 py-2 text-slate-600">{statusLabels[event.status] || paymentStatusLabels[event.status] || event.status}</td>
                        <td className="px-3 py-2 text-slate-600">{event.description || 'Sem descrição adicional'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </DialogContent>
  );
};